/**
 * Safely extracts string values from a Record object
 * @param obj - The input object of type Record<string, unknown>
 * @param keys - Array of keys to extract from the object
 * @returns An object with the requested keys, where each value is either a string (if found and valid) or undefined
 *
 * @example
 * const obj = { firstName: 'John', lastName: 'Doe', age: 30 };
 * const { firstName, lastName } = safelyGetValues(obj, ['firstName', 'lastName']);
 * // firstName = 'John', lastName = 'Doe'
 */
export function safelyGetValues<K extends string>(
  obj: Record<string, unknown>,
  keys: readonly K[],
): Record<K, string | undefined>;

export function safelyGetValues<K extends string, T>(
  obj: Record<string, unknown>,
  keys: readonly K[],
  postProcess: (value: string | undefined) => T,
): Record<K, T>;

export function safelyGetValues<K extends string, T>(
  obj: Record<string, unknown>,
  keys: readonly K[],
  postProcess?: (value: string | undefined) => T,
): Record<K, string | undefined | T> {
  const result: Record<string, string | undefined | T> = {};

  for (const key of keys) {
    const value = obj[key];
    const stringValue = typeof value === 'string' ? value : undefined;
    result[key] = postProcess ? postProcess(stringValue) : stringValue;
  }

  return result;
}
